import React, { useEffect, useState, useRef } from 'react';
import { Bell, MessageSquare, BookOpen, FileText, FileCheck, AlertCircle, CheckCircle, ChevronDown, X, Users, Award, BarChart, Calendar, Clock, Info, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
// Define notification types
type NotificationType = 'forum' | 'homework' | 'exam' | 'report' | 'system' | 'announcement' | 'program';
// Define notification filter options
type NotificationFilter = 'all' | 'forum' | 'homework_program' | 'reports' | 'system';
// Define notification interface
interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  time: string;
  read: boolean;
  link: string;
  date: Date;
}
export function NotificationsHistory() {
  // State for notifications
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<Notification[]>([]);
  const [visibleNotifications, setVisibleNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // Refs
  const observer = useRef<IntersectionObserver | null>(null);
  const lastNotificationRef = useRef<HTMLDivElement | null>(null);
  const filterRef = useRef<HTMLDivElement | null>(null);
  // Load notifications on mount
  useEffect(() => {
    // In a real app, this would be an API call
    // For now, we'll generate mock data
    const mockNotifications: Notification[] = generateMockNotifications(100);
    setNotifications(mockNotifications);
    setFilteredNotifications(mockNotifications);
    setVisibleNotifications(mockNotifications.slice(0, 20));
    setHasMore(mockNotifications.length > 20);
  }, []);
  // Apply filter when it changes
  useEffect(() => {
    const filtered = applyFilter(notifications, filter);
    setFilteredNotifications(filtered);
    setVisibleNotifications(filtered.slice(0, 20));
    setPage(1);
    setHasMore(filtered.length > 20);
  }, [filter, notifications]);
  // Handle infinite scroll
  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreNotifications();
      }
    });
    if (lastNotificationRef.current) {
      observer.current.observe(lastNotificationRef.current);
    }
  }, [loading, hasMore, visibleNotifications]);
  // Handle clicks outside the filter dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilterDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Load more notifications
  const loadMoreNotifications = () => {
    setLoading(true);
    setTimeout(() => {
      const nextPage = page + 1;
      const nextBatch = filteredNotifications.slice(0, nextPage * 20);
      setVisibleNotifications(nextBatch);
      setPage(nextPage);
      setHasMore(nextBatch.length < filteredNotifications.length);
      setLoading(false);
    }, 500); // Simulate network delay
  };
  // Mark all notifications as read
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notification => ({
      ...notification,
      read: true
    })));
  };
  // Mark a notification as read
  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(notification => notification.id === id ? {
      ...notification,
      read: true
    } : notification));
  };
  // Apply filter to notifications
  const applyFilter = (notifications: Notification[], filter: NotificationFilter): Notification[] => {
    if (filter === 'all') return notifications;
    return notifications.filter(notification => {
      switch (filter) {
        case 'forum':
          return notification.type === 'forum';
        case 'homework_program':
          return ['homework', 'program'].includes(notification.type);
        case 'reports':
          return ['report', 'exam'].includes(notification.type);
        case 'system':
          return ['system', 'announcement'].includes(notification.type);
        default:
          return true;
      }
    });
  };
  // Get icon for notification type
  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'forum':
        return <MessageSquare className="w-5 h-5 text-blue-400" data-id="element-2531" />;
      case 'homework':
        return <BookOpen className="w-5 h-5 text-green-500" data-id="element-2532" />;
      case 'program':
        return <Calendar className="w-5 h-5 text-purple-400" data-id="element-2533" />;
      case 'exam':
        return <FileCheck className="w-5 h-5 text-amber-500" data-id="element-2534" />;
      case 'report':
        return <BarChart className="w-5 h-5 text-indigo-400" data-id="element-2535" />;
      case 'system':
        return <Info className="w-5 h-5 text-gray-400" data-id="element-2536" />;
      case 'announcement':
        return <AlertCircle className="w-5 h-5 text-red-400" data-id="element-2537" />;
      default:
        return <Bell className="w-5 h-5 text-gray-400" data-id="element-2538" />;
    }
  };
  // Get filter name for display
  const getFilterName = (filter: NotificationFilter): string => {
    switch (filter) {
      case 'all':
        return 'Tüm Türler';
      case 'forum':
        return 'Forum Bildirimleri';
      case 'homework_program':
        return 'Ödev ve Program';
      case 'reports':
        return 'Raporlar ve Sonuçlar';
      case 'system':
        return 'Sistem Duyuruları';
      default:
        return 'Tüm Türler';
    }
  };
  // Generate mock notifications
  const generateMockNotifications = (count: number): Notification[] => {
    const types: NotificationType[] = ['forum', 'homework', 'exam', 'report', 'system', 'announcement', 'program'];
    const mockData: Notification[] = [];
    const now = new Date();
    for (let i = 0; i < count; i++) {
      const type = types[Math.floor(Math.random() * types.length)];
      const date = new Date(now.getTime() - Math.random() * 30 * 24 * 60 * 60 * 1000);
      let title = '';
      let message = '';
      let link = '';
      switch (type) {
        case 'forum':
          title = 'Forum Bildirimi';
          message = ['Sorunuza yeni bir cevap geldi.', 'Cevabınız beğeni aldı.', 'Bir öğretmen sorunuzu yanıtladı.', 'Takip ettiğiniz konuya yeni bir yanıt geldi.'][Math.floor(Math.random() * 4)];
          link = '/forum';
          break;
        case 'homework':
          title = 'Ödev Bildirimi';
          message = ['Yeni bir ödev eklendi.', 'Ödeviniz değerlendirildi.', 'Bir ödevin teslim tarihi yaklaşıyor.', 'Ödev notunuz açıklandı.'][Math.floor(Math.random() * 4)];
          link = '/homework';
          break;
        case 'exam':
          title = 'Sınav Bildirimi';
          message = ['Yeni bir deneme sınavı eklendi.', 'Sınav sonuçlarınız açıklandı.', 'Sınav analiziniz hazır.', 'Yaklaşan bir sınavınız var.'][Math.floor(Math.random() * 4)];
          link = '/analysis';
          break;
        case 'report':
          title = 'Rapor Bildirimi';
          message = ['Haftalık ilerleme raporunuz hazır.', 'Performans analiziniz güncellendi.', 'Konu bazlı başarı raporunuz hazırlandı.', 'Yeni bir başarı grafiği oluşturuldu.'][Math.floor(Math.random() * 4)];
          link = '/progress';
          break;
        case 'system':
          title = 'Sistem Bildirimi';
          message = ['Sistemde bakım çalışması yapılacak.', 'Yeni özellikler eklendi.', 'Güvenlik güncellemesi yapıldı.', 'Uygulamayı güncelleyin.'][Math.floor(Math.random() * 4)];
          link = '/';
          break;
        case 'announcement':
          title = 'Duyuru';
          message = ['Önemli bir duyuru yayınlandı.', 'Ders programında değişiklik yapıldı.', 'Yeni bir etkinlik düzenlenecek.', 'Tatil günleri açıklandı.'][Math.floor(Math.random() * 4)];
          link = '/';
          break;
        case 'program':
          title = 'Program Bildirimi';
          message = ['Çalışma programınız güncellendi.', 'Yeni bir ders programı oluşturuldu.', 'Program değişikliği yapıldı.', 'Özel çalışma planınız hazır.'][Math.floor(Math.random() * 4)];
          link = '/schedule';
          break;
      }
      // Format the time string
      let timeStr = '';
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) {
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        if (diffHours === 0) {
          const diffMinutes = Math.floor(diffMs / (1000 * 60));
          timeStr = `${diffMinutes} dakika önce`;
        } else {
          timeStr = `${diffHours} saat önce`;
        }
      } else if (diffDays === 1) {
        timeStr = 'Dün';
      } else if (diffDays < 7) {
        timeStr = `${diffDays} gün önce`;
      } else {
        timeStr = `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
      }
      mockData.push({
        id: `notification-${i}`,
        type,
        title,
        message,
        time: timeStr,
        read: Math.random() > 0.3,
        link,
        date
      });
    }
    // Sort by date, newest first
    return mockData.sort((a, b) => b.date.getTime() - a.date.getTime());
  };
  // Count unread notifications
  const unreadCount = notifications.filter(n => !n.read).length;
  return <div className="space-y-6" data-id="element-2539">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-2540">
        <h1 className="text-2xl font-bold" data-id="element-2541">Tüm Bildirimler</h1>
        <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-2542">
          {unreadCount > 0 && <button onClick={markAllAsRead} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors" data-id="element-2543">
              <CheckCircle className="w-4 h-4" data-id="element-2544" />
              <span data-id="element-2545">Tümünü Okundu İşaretle</span>
            </button>}
        </div>
      </div>
      {/* Filter and Controls */}
      <div className="flex flex-wrap gap-3 mb-4" ref={filterRef} data-id="element-2546">
        <div className="relative" data-id="element-2547">
          <button onClick={() => setShowFilterDropdown(!showFilterDropdown)} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors" data-id="element-2548">
            <span data-id="element-2549">Filtrele: </span>
            <span className="text-purple-400" data-id="element-2550">{getFilterName(filter)}</span>
            <ChevronDown className="w-4 h-4 ml-1" data-id="element-2551" />
          </button>
          {showFilterDropdown && <div className="absolute left-0 z-10 mt-1 w-64 bg-black/90 rounded-lg shadow-lg border border-gray-800 animate-fadeIn" data-id="element-2552">
              <button onClick={() => {
            setFilter('all');
            setShowFilterDropdown(false);
          }} className={`w-full px-4 py-3 text-left hover:bg-white/10 flex items-center gap-2 ${filter === 'all' ? 'bg-purple-500/20 text-purple-400' : ''}`} data-id="element-2553">
                <Bell className="w-4 h-4" data-id="element-2554" />
                <span data-id="element-2555">Tüm Türler</span>
              </button>
              <button onClick={() => {
            setFilter('forum');
            setShowFilterDropdown(false);
          }} className={`w-full px-4 py-3 text-left hover:bg-white/10 flex items-center gap-2 ${filter === 'forum' ? 'bg-purple-500/20 text-purple-400' : ''}`} data-id="element-2556">
                <MessageSquare className="w-4 h-4" data-id="element-2557" />
                <span data-id="element-2558">Forum Bildirimleri</span>
              </button>
              <button onClick={() => {
            setFilter('homework_program');
            setShowFilterDropdown(false);
          }} className={`w-full px-4 py-3 text-left hover:bg-white/10 flex items-center gap-2 ${filter === 'homework_program' ? 'bg-purple-500/20 text-purple-400' : ''}`} data-id="element-2559">
                <BookOpen className="w-4 h-4" data-id="element-2560" />
                <span data-id="element-2561">Ödev ve Program</span>
              </button>
              <button onClick={() => {
            setFilter('reports');
            setShowFilterDropdown(false);
          }} className={`w-full px-4 py-3 text-left hover:bg-white/10 flex items-center gap-2 ${filter === 'reports' ? 'bg-purple-500/20 text-purple-400' : ''}`} data-id="element-2562">
                <BarChart className="w-4 h-4" data-id="element-2563" />
                <span data-id="element-2564">Raporlar ve Sonuçlar</span>
              </button>
              <button onClick={() => {
            setFilter('system');
            setShowFilterDropdown(false);
          }} className={`w-full px-4 py-3 text-left hover:bg-white/10 flex items-center gap-2 ${filter === 'system' ? 'bg-purple-500/20 text-purple-400' : ''}`} data-id="element-2565">
                <Info className="w-4 h-4" data-id="element-2566" />
                <span data-id="element-2567">Sistem Duyuruları</span>
              </button>
            </div>}
        </div>
        {filter !== 'all' && <button onClick={() => setFilter('all')} className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors" data-id="element-2568">
            <X className="w-4 h-4" data-id="element-2569" />
            <span data-id="element-2570">Filtreyi Temizle</span>
          </button>}
      </div>
      {/* Notifications List */}
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2571">
        <div className="relative p-6 bg-black/90 rounded-xl" data-id="element-2572">
          <div className="space-y-2" data-id="element-2573">
            {visibleNotifications.length > 0 ? visibleNotifications.map((notification, index) => {
            // Determine if this is the last item
            const isLastItem = index === visibleNotifications.length - 1;
            return <div key={notification.id} ref={isLastItem ? lastNotificationRef : null} className={`p-4 rounded-lg transition-colors flex items-center gap-4 ${notification.read ? 'bg-black/50' : 'bg-white/5 border-l-4 border-purple-500'}`} data-id="element-2574">
                    <Link to={notification.link} className="flex items-center gap-4 w-full" onClick={() => markAsRead(notification.id)} data-id="element-2575">
                      <div className="flex-shrink-0" data-id="element-2576">
                        <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center" data-id="element-2577">
                          {getNotificationIcon(notification.type)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2578">
                        <div className="flex items-center gap-2" data-id="element-2579">
                          <h3 className={`font-medium ${notification.read ? '' : 'text-white'}`} data-id="element-2580">
                            {notification.title}
                          </h3>
                          {!notification.read && <span className="w-2 h-2 rounded-full bg-purple-500" data-id="element-2581"></span>}
                        </div>
                        <p className={`text-sm ${notification.read ? 'text-gray-400' : 'text-gray-300'}`} data-id="element-2582">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-2583">
                          {notification.time}
                        </p>
                      </div>
                      <div className="flex-shrink-0" data-id="element-2584">
                        <ExternalLink className="w-4 h-4 text-gray-400" data-id="element-2585" />
                      </div>
                    </Link>
                  </div>;
          }) : <div className="text-center py-12 text-gray-400" data-id="element-2586">
                {filter === 'all' ? 'Henüz bildirim bulunmuyor.' : 'Bu türde bildirim bulunmuyor.'}
              </div>}
            {/* Loading indicator */}
            {loading && <div className="text-center py-4" data-id="element-2587">
                <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-purple-500 border-r-transparent" data-id="element-2588"></div>
                <p className="mt-2 text-sm text-gray-400" data-id="element-2589">Yükleniyor...</p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}